import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Trash2, Waves, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  getSubseaProfile,
  saveSubseaProfile,
  clearSubseaProfile,
  SUBSEA_PROFILE_FIELDS,
  SUBSEA_PROFILE_GROUPS,
} from "@/lib/storage";
import { SafetyBanner } from "@/components/SafetyBanner";

const VERIFY_TEXT =
  "Subsea profile data must be verified against rig-specific equipment, approved well-control procedures, company documentation, and competent personnel before use.";

export default function SubseaProfile() {
  const navigate = useNavigate();
  const existing = getSubseaProfile();
  const [form, setForm] = useState(() => existing || emptyForm());
  const [isEditing, setIsEditing] = useState(!existing);   // when no profile exists, start in edit mode
  const [savedAt, setSavedAt] = useState(existing?.updatedAt || null);

  const setF = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleSave = () => {
    const out = saveSubseaProfile(form);
    setSavedAt(out.updatedAt);
    setIsEditing(false);
    toast.success("Subsea Well Control Profile saved");
  };
  const handleDelete = () => {
    if (!window.confirm("Delete the saved Subsea Well Control Profile? This cannot be undone.")) return;
    clearSubseaProfile();
    setForm(emptyForm());
    setSavedAt(null);
    setIsEditing(true);
    toast.success("Subsea Well Control Profile cleared");
  };
  const handleEdit = () => setIsEditing(true);
  const handleCancel = () => {
    const cur = getSubseaProfile();
    setForm(cur || emptyForm());
    setIsEditing(false);
  };

  const grouped = useMemo(() => {
    const map = {};
    SUBSEA_PROFILE_FIELDS.forEach((f) => {
      if (!map[f.group]) map[f.group] = [];
      map[f.group].push(f);
    });
    return map;
  }, []);

  return (
    <div className="flex flex-col gap-5" data-testid="subsea-profile-page">
      <button
        data-testid="subsea-profile-back"
        onClick={() => navigate("/categories/wellcontrol/subsea")}
        className="btn-ghost h-9 px-3 rounded-md inline-flex items-center gap-2 text-xs label-up w-fit"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Subsea Well Control
      </button>

      <section>
        <div className="font-mono-rs text-[10px] tracking-[0.22em] uppercase" style={{ color: "#60A5FA" }}>
          Subsea Well Control
        </div>
        <div className="flex items-baseline gap-2 flex-wrap mt-1">
          <Waves className="w-6 h-6" style={{ color: "#60A5FA" }} />
          <h2 className="font-heading font-black text-3xl tracking-tight">Subsea Well Control Profile</h2>
        </div>
        <p className="text-sm text-[var(--rs-text-2)] mt-1.5 leading-relaxed">
          Capture rig-specific subsea stack data so upcoming subsea calculators and worksheets can pull values
          instead of asking for them every time. Profile use is always optional.
        </p>
      </section>

      {/* RC-1 — Deprecation banner. Subsea stack data has moved into the Rig Data Sheet's
          `payload.subsea` block, which the Engineering Context Engine reads natively.
          This standalone Subsea Profile is being retired in V2. */}
      <div
        data-testid="subsea-profile-deprecated-banner"
        className="card-rs p-3"
        style={{
          background:  "rgba(244,67,54,0.06)",
          borderColor: "#C62828",
          borderLeft:  "2px solid #C62828",
        }}
      >
        <div className="text-[10px] tracking-[0.18em] uppercase font-semibold mb-1 text-[#FCA5A5]">
          Deprecated · Use Rig Data Sheet
        </div>
        <p className="text-[11.5px] text-[var(--rs-text-2)] leading-snug">
          Subsea stack data now lives on the Rig Data Sheet&apos;s subsea section, which the
          Engineering Context Engine reads automatically. Please use the canonical Rig
          Data Sheet editor instead. This standalone Subsea Profile is read-only going
          forward and will be removed in V2.
        </p>
      </div>

      <SafetyBanner subsea />
      <div
        data-testid="subsea-profile-verify-banner"
        className="flex gap-2 p-3 rounded-md border"
        style={{ background: "rgba(255,193,7,0.07)", borderColor: "#F57F17" }}
      >
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--rs-warning)" }} />
        <p className="text-xs leading-snug text-[#FBD38D]">{VERIFY_TEXT}</p>
      </div>

      {savedAt && !isEditing && (
        <div
          className="flex items-center gap-2 p-3 rounded-md border"
          style={{ background: "rgba(56,142,60,0.10)", borderColor: "var(--rs-success)" }}
          data-testid="subsea-profile-saved-banner"
        >
          <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: "var(--rs-success)" }} />
          <div className="text-xs text-[#A7F3D0]">
            Profile saved locally · last updated {new Date(savedAt).toLocaleString()}
          </div>
        </div>
      )}

      {SUBSEA_PROFILE_GROUPS.map((g) => (
        <section key={g.id} className="card-rs p-4" data-testid={`subsea-group-${g.id}`}>
          <div className="label-up text-[var(--rs-amber)] mb-3">{g.title}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(grouped[g.id] || []).map((f) => (
              <Field
                key={f.key}
                field={f}
                value={form[f.key]}
                readOnly={!isEditing}
                onChange={(v) => setF(f.key, v)}
              />
            ))}
          </div>
        </section>
      ))}

      <section className="flex flex-wrap items-center gap-2">
        {isEditing ? (
          <>
            <button
              data-testid="subsea-profile-save"
              onClick={handleSave}
              className="btn-amber h-11 px-4 rounded-md inline-flex items-center gap-2 text-xs label-up"
            >
              <Save className="w-4 h-4" /> {savedAt ? "Update Profile" : "Save Profile"}
            </button>
            {savedAt && (
              <button
                data-testid="subsea-profile-cancel"
                onClick={handleCancel}
                className="btn-ghost h-11 px-4 rounded-md inline-flex items-center gap-2 text-xs label-up"
              >
                Cancel
              </button>
            )}
          </>
        ) : (
          <button
            data-testid="subsea-profile-edit"
            onClick={handleEdit}
            className="btn-amber h-11 px-4 rounded-md inline-flex items-center gap-2 text-xs label-up"
          >
            Edit Profile
          </button>
        )}
        {savedAt && (
          <button
            data-testid="subsea-profile-delete"
            onClick={handleDelete}
            className="btn-ghost h-11 px-4 rounded-md inline-flex items-center gap-2 text-xs label-up ml-auto"
            style={{ borderColor: "#C62828", color: "#FCA5A5" }}
          >
            <Trash2 className="w-4 h-4" /> Delete Profile
          </button>
        )}
      </section>
    </div>
  );
}

function Field({ field, value, onChange, readOnly }) {
  const label = field.unit ? `${field.label} (${field.unit})` : field.label;
  return (
    <label className="block">
      <div className="text-[11px] text-[var(--rs-text-3)] uppercase tracking-[0.1em] mb-1">{label}</div>
      {field.multiline ? (
        <textarea
          data-testid={`subsea-field-${field.key}`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          readOnly={readOnly}
          className="input-rs"
        />
      ) : (
        <input
          data-testid={`subsea-field-${field.key}`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          className="input-rs"
        />
      )}
    </label>
  );
}

function emptyForm() {
  const o = {};
  SUBSEA_PROFILE_FIELDS.forEach((f) => { o[f.key] = ""; });
  return o;
}
